/**
 * Admin v2 endpoints — port of v1 ``ando-frontend/app/pages/HypermemAdminPage.tsx``
 * (398 LoC) + ``OrganizationsPage.tsx`` (3403 LoC monolith — v2 rewrite authorized
 * per memory rule "v2 rewrite authorized for monoliths >1500 LoC").
 *
 * BE source of truth:
 *   - ``ando-agent/src/api/v1/endpoints/admin_v2.py``
 *   - ``ando-agent/src/api/v1/endpoints/admin_v2_models.py`` (Pydantic models)
 *
 * Route prefix on the wire is ``/admin/v2/*`` — every endpoint is gated by
 * ``require_platform_admin`` server-side. The FE additionally hides the
 * route group with ``useHasCapability("admin")`` so non-admin users never
 * see the entry point.
 *
 * Privacy contract: every endpoint returns AGGREGATE data by default. PII
 * (email / user identities) is only revealed by ``getUserTimeline`` when
 * Inspect Mode is active for the (admin, tenant, scope) triple.
 */

import { apiClient } from "../ApiClient.js";

// ── Shared primitives (mirror admin_v2_models.py 1:1) ────────────────────

export interface AdminTimeSeriesPoint {
  bucket_start: string;
  value: number;
  anomaly?: boolean;
}

export interface AdminStackedAreaSeries {
  label: string;
  points: AdminTimeSeriesPoint[];
}

export interface AdminKpiCard {
  label: string;
  value: number;
  previous_value?: number | null;
  delta_pct?: number | null;
  unit: string;
  sparkline?: AdminTimeSeriesPoint[];
}

export interface AdminTopNRow {
  id: string;
  label: string;
  value: number;
  unit?: string;
  sparkline?: AdminTimeSeriesPoint[];
}

export interface AdminCohortRow {
  cohort_start: string;
  size: number;
  retention_pct: number[];
}

export interface AdminHeatmapCell {
  x: string;
  y: string;
  value: number;
}

export interface AdminHistogramBin {
  lower: number;
  /** ``null`` = open tail bucket (≥ lower). */
  upper: number | null;
  count: number;
}

export interface AdminAnomalyFlag {
  metric: string;
  severity: "info" | "warning" | "critical";
  summary: string;
  detected_at: string;
  deviation_pct?: number | null;
}

export interface AdminTimeRange {
  start: string;
  end: string;
  previous_start: string;
  previous_end: string;
  granularity: "hour" | "day" | "week" | "month";
}

export interface AdminEnvelope {
  time_range: AdminTimeRange;
  inspect_mode_active: boolean;
}

// ── A1 — Overview ────────────────────────────────────────────────────────

export interface AdminOverviewResponse extends AdminEnvelope {
  kpis: AdminKpiCard[];
  model_mix: AdminStackedAreaSeries[];
  top_orgs_by_cost: AdminTopNRow[];
  anomalies: AdminAnomalyFlag[];
}

// ── A2 — Engagement ──────────────────────────────────────────────────────

export interface AdminFunnelStep {
  step: string;
  count: number;
  conversion_from_prev_pct?: number | null;
}

export interface AdminFeatureAdoption {
  feature: string;
  wau: number;
  wau_pct_of_active: number;
  delta_pct?: number | null;
}

export interface AdminStickinessRow {
  dau: number;
  wau: number;
  mau: number;
  stickiness_pct: number;
}

export interface AdminTopActiveUser {
  user_id: string;
  email: string | null;
  name: string | null;
  query_count: number;
  last_active: string | null;
}

export interface AdminTimeToFirstValue {
  ttfv_first_query_min: number | null;
  ttfv_first_canvas_min: number | null;
}

export interface AdminEarlyChurnSignal {
  lost_users: number;
  recovered_users: number;
  prev_wau: number;
  cur_wau: number;
}

export interface AdminActivityHeatmapCell {
  dow: number;
  hour: number;
  value: number;
}

export interface AdminFeatureBreadthBucket {
  bucket: string;
  user_count: number;
  pct: number;
}

export interface AdminEngagementResponse extends AdminEnvelope {
  funnel: AdminFunnelStep[];
  retention_cohorts: AdminCohortRow[];
  feature_adoption: AdminFeatureAdoption[];
  depth_of_use: Record<string, number>;
  stickiness: AdminStickinessRow;
  top_active_users: AdminTopActiveUser[];
  time_to_first_value: AdminTimeToFirstValue;
  early_churn: AdminEarlyChurnSignal;
  activity_heatmap: AdminActivityHeatmapCell[];
  feature_breadth: AdminFeatureBreadthBucket[];
}

// ── A3 — Cost ────────────────────────────────────────────────────────────

export interface AdminCostBySource {
  source: string;
  cost_usd: number;
  tokens_input: number;
  tokens_output: number;
  tokens_cached: number;
}

export interface AdminCostPerOutcome {
  outcome: string;
  avg_cost_usd: number;
  count: number;
  total_cost_usd: number;
}

export interface AdminGrossMarginRow {
  org_id: string;
  org_name: string;
  mrr_usd: number;
  inference_cost_usd: number;
  gross_margin_pct: number | null;
  cost_per_active_user_usd: number;
}

export interface AdminCostCompositionBucket {
  bucket_start: string | null;
  input_cost: number;
  cached_cost: number;
  output_cost: number;
  thinking_cost: number;
  reasoning_cost: number;
}

export interface AdminExpensiveTraceRow {
  trace_id: string;
  user_id: string | null;
  tenant_id: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  call_count: number;
  model_count: number;
  models: string[];
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  thinking_tokens: number;
  reasoning_tokens: number;
}

/** One actionable cost-optimization finding (admin v2.7). */
export interface AdminCostOpportunity {
  id: string;
  title: string;
  detail: string;
  impact: "high" | "medium" | "low";
  est_savings_usd: number;
  lever: "caching" | "model_routing" | "retry_fix" | "context_trim" | string;
  evidence: Record<string, unknown>;
}

export interface AdminCostResponse extends AdminEnvelope {
  kpis: AdminKpiCard[];
  cost_by_source: AdminCostBySource[];
  cost_per_outcome: AdminCostPerOutcome[];
  model_mix_timeline: AdminStackedAreaSeries[];
  gross_margin_per_tenant: AdminGrossMarginRow[];
  cost_composition_timeline: AdminCostCompositionBucket[];
  top_expensive_traces: AdminExpensiveTraceRow[];
  cost_opportunities: AdminCostOpportunity[];
}

// ── A3c — Per-ORG cost drill-down (Jun 5, 2026) ──────────────────────────

export interface AdminOrgCostCategorySplit {
  chat: number;
  ingestion: number;
  profiling: number;
}

export interface AdminOrgCostSummary {
  total_usd: number;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  thinking_tokens: number;
  by_category: AdminOrgCostCategorySplit;
}

export interface AdminOrgCostDimensionRow {
  label: string;
  cost_usd: number;
  calls: number;
  input_tokens: number;
  output_tokens: number;
}

export interface AdminOrgCostUserRow {
  user_id: string | null;
  email: string | null;
  cost_usd: number;
  calls: number;
}

export interface AdminOrgCostResponse {
  time_range: AdminTimeRange;
  org_id: string;
  org_name: string | null;
  kpis: AdminKpiCard[];
  summary: AdminOrgCostSummary;
  cost_timeseries: AdminTimeSeriesPoint[];
  by_category: AdminOrgCostDimensionRow[];
  by_source: AdminOrgCostDimensionRow[];
  by_model: AdminOrgCostDimensionRow[];
  by_provider: AdminOrgCostDimensionRow[];
  by_user: AdminOrgCostUserRow[];
  inspect_mode_active: boolean;
}

// ── A3d — Google reconciliation (Jun 5, 2026) ────────────────────────────

export interface AdminCostReconciliation {
  available: boolean;
  error: string | null;
  google_input_tokens: number;
  google_output_tokens: number;
  recorded_input_tokens: number;
  recorded_output_tokens: number;
  input_coverage: number | null;
  output_coverage: number | null;
  untracked_input_tokens: number;
  untracked_output_tokens: number;
  recorded_cost_usd: number;
}

export interface AdminCostReconciliationResponse {
  time_range: AdminTimeRange;
  source: string;
  reconciliation: AdminCostReconciliation;
}

export interface AdminTraceCallDetail {
  id: string;
  parent_id: string | null;
  source: string;
  provider: string;
  model: string;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  thinking_tokens: number;
  reasoning_tokens: number;
  cost_usd: number;
  attributes?: Record<string, unknown> | null;
}

export interface AdminTraceDetailResponse {
  trace_id: string;
  calls: AdminTraceCallDetail[];
  total_cost_usd: number;
  total_call_count: number;
}

// ── A4 — Quality ─────────────────────────────────────────────────────────

export interface AdminToolUseRow {
  tool: string;
  fire_count: number;
  success_rate: number;
  p95_latency_ms: number;
  avg_cost_usd?: number | null;
}

export interface AdminQualityResponse extends AdminEnvelope {
  kpis: AdminKpiCard[];
  feedback_timeline: AdminStackedAreaSeries[];
  tool_use_top: AdminToolUseRow[];
  tool_outcome_heatmap: AdminHeatmapCell[];
}

// ── A5 — Health ──────────────────────────────────────────────────────────

export interface AdminWorkerSaturationRow {
  queue: string;
  active_workers: number;
  pending_tasks: number;
  saturation_pct?: number | null;
}

export interface AdminLatencyByRoute {
  route: string;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  request_count: number;
  error_rate_pct: number;
}

export interface AdminHealthResponse extends AdminEnvelope {
  kpis: AdminKpiCard[];
  worker_saturation: AdminWorkerSaturationRow[];
  latency_by_route: AdminLatencyByRoute[];
  latency_histogram: AdminHistogramBin[];
  /** BE schema is union — normalise to array on the wire. */
  error_rate_timeline: AdminTimeSeriesPoint[] | AdminTimeSeriesPoint;
}

// ── A6 — Canvas ──────────────────────────────────────────────────────────

export interface AdminCanvasStatusBreakdown {
  status: string;
  count: number;
}

export interface AdminTopEntityRow {
  entity_id: string;
  title: string;
  entity_type: string;
  mention_count: number;
  confidence_score: number;
  compile_status: string;
}

export interface AdminCanvasResponse extends AdminEnvelope {
  kpis: AdminKpiCard[];
  status_breakdown: AdminCanvasStatusBreakdown[];
  top_entities: AdminTopEntityRow[];
  compile_cost_timeline: AdminTimeSeriesPoint[];
  time_to_ready_histogram: AdminHistogramBin[];
}

// ── A7 — Automation ──────────────────────────────────────────────────────

export interface AdminAutomationRunBreakdown {
  status: string;
  count: number;
}

export interface AdminTopAutomationRow {
  automation_id: string;
  name: string;
  run_count: number;
  success_rate: number;
  avg_cost_usd: number;
}

export interface AdminAutomationResponse extends AdminEnvelope {
  kpis: AdminKpiCard[];
  runs_breakdown: AdminAutomationRunBreakdown[];
  runs_timeline: AdminStackedAreaSeries[];
  pending_suggestions_queue: number;
  top_automations: AdminTopAutomationRow[];
}

// ── A8 — User drill-down ─────────────────────────────────────────────────

export interface AdminUserTimelineEvent {
  timestamp: string;
  kind:
    | "query"
    | "ingestion"
    | "canvas_compile"
    | "automation_run"
    | "feedback"
    | "nudge_delivered";
  summary: string;
  cost_usd?: number | null;
  metadata?: Record<string, unknown>;
}

export interface AdminUserTimelineResponse extends AdminEnvelope {
  user_id: string;
  email?: string | null;
  kpis: AdminKpiCard[];
  timeline: AdminUserTimelineEvent[];
  cost_timeline: AdminTimeSeriesPoint[];
}

// ── A9 — Inspect Mode ────────────────────────────────────────────────────

export interface AdminInspectModeStatus {
  active: boolean;
  activated_at?: string | null;
  expires_at?: string | null;
  scope_key?: string | null;
  tenant_id?: string | null;
}

// ── Zitadel account ops ──────────────────────────────────────────────────

export interface AdminZitadelAccountState {
  zitadel_user_id?: string | null;
  state: string;
  is_locked: boolean;
}

export interface AdminZitadelUnlockResponse {
  user_id: string;
  zitadel_user_id: string;
  unlocked_at: string;
  actor_id: string;
}

export interface AdminZitadelResetPasswordResponse {
  user_id: string;
  zitadel_user_id: string;
  email?: string | null;
  sent_at: string;
  actor_id: string;
}

export interface AdminZitadelSetPasswordRequest {
  password: string;
  change_required: boolean;
}

export interface AdminZitadelSetPasswordResponse {
  user_id: string;
  zitadel_user_id: string;
  change_required: boolean;
  set_at: string;
  actor_id: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────

interface RangeArgs {
  /** ISO start datetime. BE defaults the window when both are omitted. */
  start?: string;
  end?: string;
}

function rangeQs(args: RangeArgs): string {
  const params = new URLSearchParams();
  if (args.start) params.set("start", args.start);
  if (args.end) params.set("end", args.end);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

// ── Endpoint helpers ────────────────────────────────────────────────────

/** GET ``/admin/v2/overview`` */
export async function getAdminOverview(args: RangeArgs = {}): Promise<AdminOverviewResponse> {
  return apiClient.get<AdminOverviewResponse>(`/admin/v2/overview${rangeQs(args)}`);
}

/** GET ``/admin/v2/engagement`` */
export async function getAdminEngagement(args: RangeArgs = {}): Promise<AdminEngagementResponse> {
  return apiClient.get<AdminEngagementResponse>(`/admin/v2/engagement${rangeQs(args)}`);
}

/** GET ``/admin/v2/cost`` */
export async function getAdminCost(args: RangeArgs = {}): Promise<AdminCostResponse> {
  return apiClient.get<AdminCostResponse>(`/admin/v2/cost${rangeQs(args)}`);
}

/** GET ``/admin/v2/cost/reconciliation`` — recorded vs Google authoritative. */
export async function getAdminCostReconciliation(
  args: RangeArgs = {},
): Promise<AdminCostReconciliationResponse> {
  return apiClient.get<AdminCostReconciliationResponse>(
    `/admin/v2/cost/reconciliation${rangeQs(args)}`,
  );
}

/** GET ``/admin/v2/orgs/{org_id}/cost`` — hyper-precise per-org cost drill-down. */
export async function getAdminOrgCost(
  orgId: string,
  args: RangeArgs = {},
): Promise<AdminOrgCostResponse> {
  return apiClient.get<AdminOrgCostResponse>(
    `/admin/v2/orgs/${encodeURIComponent(orgId)}/cost${rangeQs(args)}`,
  );
}

/** GET ``/admin/v2/cost/trace/{trace_id}`` — drill-down. */
export async function getAdminCostTraceDetail(traceId: string): Promise<AdminTraceDetailResponse> {
  return apiClient.get<AdminTraceDetailResponse>(
    `/admin/v2/cost/trace/${encodeURIComponent(traceId)}`,
  );
}

/** GET ``/admin/v2/quality`` */
export async function getAdminQuality(args: RangeArgs = {}): Promise<AdminQualityResponse> {
  return apiClient.get<AdminQualityResponse>(`/admin/v2/quality${rangeQs(args)}`);
}

/** GET ``/admin/v2/health`` */
export async function getAdminHealth(args: RangeArgs = {}): Promise<AdminHealthResponse> {
  return apiClient.get<AdminHealthResponse>(`/admin/v2/health${rangeQs(args)}`);
}

/** GET ``/admin/v2/canvas`` */
export async function getAdminCanvas(args: RangeArgs = {}): Promise<AdminCanvasResponse> {
  return apiClient.get<AdminCanvasResponse>(`/admin/v2/canvas${rangeQs(args)}`);
}

/** GET ``/admin/v2/automation`` */
export async function getAdminAutomation(args: RangeArgs = {}): Promise<AdminAutomationResponse> {
  return apiClient.get<AdminAutomationResponse>(`/admin/v2/automation${rangeQs(args)}`);
}

/** GET ``/admin/v2/users/{user_id}/timeline`` (PII-gated). */
export async function getAdminUserTimeline(args: {
  userId: string;
  tenantId?: string;
  start?: string;
  end?: string;
}): Promise<AdminUserTimelineResponse> {
  const params = new URLSearchParams();
  if (args.start) params.set("start", args.start);
  if (args.end) params.set("end", args.end);
  if (args.tenantId) params.set("tenant_id", args.tenantId);
  const qs = params.toString();
  return apiClient.get<AdminUserTimelineResponse>(
    `/admin/v2/users/${encodeURIComponent(args.userId)}/timeline${qs ? `?${qs}` : ""}`,
  );
}

/** POST ``/admin/v2/inspect`` — activate Inspect Mode (15 min TTL). */
export async function activateInspectMode(body: {
  tenant_id: string;
  scope_key: string;
  reason?: string;
}): Promise<AdminInspectModeStatus> {
  return apiClient.post<AdminInspectModeStatus>("/admin/v2/inspect", body);
}

/** GET ``/admin/v2/inspect?tenant_id=&scope_key=`` — current state. */
export async function getInspectModeStatus(args: {
  tenant_id: string;
  scope_key: string;
}): Promise<AdminInspectModeStatus> {
  const params = new URLSearchParams({
    tenant_id: args.tenant_id,
    scope_key: args.scope_key,
  });
  return apiClient.get<AdminInspectModeStatus>(`/admin/v2/inspect?${params.toString()}`);
}

/** GET ``/admin/v2/users/{user_id}/zitadel-state`` — read-only lock state. */
export async function getZitadelAccountState(userId: string): Promise<AdminZitadelAccountState> {
  return apiClient.get<AdminZitadelAccountState>(
    `/admin/v2/users/${encodeURIComponent(userId)}/zitadel-state`,
  );
}

/** POST ``/admin/v2/users/{user_id}/zitadel-unlock`` — unlock a locked user. */
export async function unlockZitadelAccount(userId: string): Promise<AdminZitadelUnlockResponse> {
  return apiClient.post<AdminZitadelUnlockResponse>(
    `/admin/v2/users/${encodeURIComponent(userId)}/zitadel-unlock`,
    {},
  );
}

/** POST ``/admin/v2/users/{user_id}/zitadel-reset-password`` — email a reset link. */
export async function sendZitadelPasswordReset(
  userId: string,
): Promise<AdminZitadelResetPasswordResponse> {
  return apiClient.post<AdminZitadelResetPasswordResponse>(
    `/admin/v2/users/${encodeURIComponent(userId)}/zitadel-reset-password`,
    {},
  );
}

/** POST ``/admin/v2/users/{user_id}/zitadel-set-password`` — admin sets a new password. */
export async function setZitadelPassword(
  userId: string,
  body: AdminZitadelSetPasswordRequest,
): Promise<AdminZitadelSetPasswordResponse> {
  return apiClient.post<AdminZitadelSetPasswordResponse>(
    `/admin/v2/users/${encodeURIComponent(userId)}/zitadel-set-password`,
    body,
  );
}

// ── Admin Users list (legacy ``/admin/users`` — pre-v2 endpoint) ─────────
//
// The v1 admin Users page was wired to ``GET /admin/users`` (Postgres-only
// query, paginated, filtered by role/status/search). v2 reuses the SAME
// wire contract — there is no plan to migrate this list to
// ``/admin/v2/...`` because the page-level concern (list + edit + drill-
// down) is orthogonal to the dashboard aggregates that own the ``v2/`` prefix.
//
// BE source: ``ando-agent/src/api/v1/endpoints/admin.py`` lines 232-368.
// PII contract: ``email`` + ``display_name`` come down RAW (this endpoint
// is the platform-admin source-of-truth for user contact data — the
// PII-aggregate boundary lives on ``/admin/v2/*``).

/** Assignable surface role — single source of truth for admin UI + requests.
 * ``platform-dev`` = console-only (API/dev surface, no product). */
export type PlatformRole = "user" | "platform-dev" | "admin" | "superadmin";

export interface AdminUser {
  user_id: string;
  email: string;
  display_name: string | null;
  /** Free / Starter / Plus / Enterprise — BE returns string for forward-compat. */
  tier: string;
  role: PlatformRole;
  /** ``active`` | ``suspended`` | ``deleted`` — BE schema ``UpdateUserRequest``. */
  status: "active" | "suspended" | "deleted";
  queries_this_month: number;
  documents_indexed: number;
  created_at: string | null;
  last_login: string | null;
  /** Legacy fields preserved for forward-compat. */
  queries_used: number;
  queries_limit: number;
}

export interface AdminUserListResponse {
  users: AdminUser[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface ListAdminUsersOpts {
  search?: string;
  role?: string;
  status?: string;
  /** Page (1-indexed). */
  page?: number;
  /** Items per page — BE caps at 100. */
  limit?: number;
}

/** GET ``/admin/users`` — paginated list with filters. */
export async function listAdminUsers(
  opts: ListAdminUsersOpts = {},
): Promise<AdminUserListResponse> {
  const params = new URLSearchParams();
  if (opts.search) params.set("search", opts.search);
  if (opts.role) params.set("role", opts.role);
  if (opts.status) params.set("status", opts.status);
  if (opts.page) params.set("page", String(opts.page));
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return apiClient.get<AdminUserListResponse>(`/admin/users${qs ? `?${qs}` : ""}`);
}

/** GET ``/admin/users/{user_id}`` — single user with subscription state. */
export async function getAdminUser(userId: string): Promise<AdminUser> {
  return apiClient.get<AdminUser>(`/admin/users/${encodeURIComponent(userId)}`);
}

export interface UpdateAdminUserRequest {
  role?: PlatformRole;
  status?: "active" | "suspended" | "deleted";
  tier?: string;
}

/** PUT ``/admin/users/{user_id}`` — update role / status / tier. */
export async function updateAdminUser(userId: string, body: UpdateAdminUserRequest): Promise<void> {
  await apiClient.put(`/admin/users/${encodeURIComponent(userId)}`, body);
}

/** Suspend — wraps ``PUT /admin/users/{id}`` with ``status=suspended``. */
export async function suspendUser(userId: string, reason: string): Promise<void> {
  // The reason is currently a client-side audit hint; BE logs the
  // ``actor_id`` automatically via ``require_platform_admin`` but does
  // not consume the body's free-form reason. When the BE adds an
  // ``audit_reason`` field, plumb it through this PUT body.
  void reason;
  await updateAdminUser(userId, { status: "suspended" });
}

/** Reactivate — wraps ``PUT /admin/users/{id}`` with ``status=active``. */
export async function activateUser(userId: string): Promise<void> {
  await updateAdminUser(userId, { status: "active" });
}

// ── Revenue (legacy ``/admin/revenue`` — pre-v2) ─────────────────────────
//
// BE source: ``ando-agent/src/api/v1/endpoints/admin.py`` ``/revenue`` route
// (lines 404-458). Aggregated MRR / ARR / churn / LTV / paying-customers,
// computed from the local subscription tables (Stripe API import is a
// future migration — see admin.py:411). Wire contract documented inline
// on the response model (``RevenueResponse``).
export interface AdminRevenueMetrics {
  mrr: number;
  arr: number;
  revenue_by_tier: Record<string, number>;
  churn_rate: number;
  ltv: number;
  paying_customers: number;
  trial_users: number;
  free_users: number;
  mrr_growth: number;
  arpu: number;
}

/** GET ``/admin/revenue`` — MRR / ARR / churn / LTV. */
export async function getAdminRevenue(): Promise<AdminRevenueMetrics> {
  return apiClient.get<AdminRevenueMetrics>("/admin/revenue");
}

// ── BE gap stubs — May 2026 ──────────────────────────────────────────────
//
// The v1 Users page wired the following destructive flows to BE endpoints
// that DON'T exist in the current ``ando-agent`` tree:
//
//   POST /admin/users/{id}/reset-sessions       — revoke all active
//   POST /admin/users/{id}/force-mfa            — require MFA at next login
//   POST /admin/users/{id}/resend-invite        — re-send verification email
//   POST /admin/users/{id}/pii-inspect/elevate  — per-user PII unlock
//
// ``/admin/v2/inspect`` exists but is tenant-scoped (different shape).
// The FE renders these action buttons DISABLED with a "BE in arrivo"
// tooltip; the helpers below throw so any accidental call surfaces
// during dev rather than silently no-oping in prod.

export async function resetUserSessions(userId: string): Promise<void> {
  void userId;
  throw new Error("BE_GAP: POST /admin/users/{id}/reset-sessions not yet implemented");
}

export async function forceMfaEnrollment(userId: string): Promise<void> {
  void userId;
  throw new Error("BE_GAP: POST /admin/users/{id}/force-mfa not yet implemented");
}

export async function resendInvite(userId: string): Promise<void> {
  void userId;
  throw new Error("BE_GAP: POST /admin/users/{id}/resend-invite not yet implemented");
}

export async function elevatePiiInspect(
  userId: string,
  reason: string,
): Promise<{ token: string; expires_at: string }> {
  // BE has ``/admin/v2/inspect`` (tenant-scoped, 15-min) but no
  // per-user ``/pii-inspect/elevate`` shape. Until the BE ships
  // a per-user variant, the FE drives a CLIENT-SIDE 5-minute
  // counter (audit trail is logged via the v2 ``/inspect``
  // endpoint when one is available).
  void userId;
  void reason;
  throw new Error("BE_GAP: POST /admin/users/{id}/pii-inspect/elevate not yet implemented");
}

// ── Platform Settings — Pricing models + Usage tier plans CRUD ───────────
//
// Wire to the SAME BE endpoints used by the v1 admin Settings tab:
//   - GET  /admin/settings/pricing          → ``PricingResponse``
//   - PUT  /admin/settings/pricing          → save model pricing config
//   - GET  /admin/plans                     → list usage tier plans
//   - POST /admin/plans                     → create a new plan
//   - PUT  /admin/plans/{id}                → patch a plan
//   - DELETE /admin/plans/{id}              → remove a plan
//
// These endpoints predate the ``/admin/v2/*`` aggregate surface — they
// are CRUD over platform-wide config so they live on the plain
// ``/admin/*`` prefix. v2 just consumes them; no migration planned.

export interface AdminModelPricing {
  input: number;
  output: number;
}

export interface AdminJinaPricing {
  embeddings_v4: { per_1m_tokens: number };
  reranker_v3: { per_1k_queries: number };
  reranker_m0: { per_1k_queries: number };
}

export interface AdminTavilyPricing {
  search: { per_search: number };
}

export interface AdminPricingConfig {
  openrouter: Record<string, AdminModelPricing>;
  google: Record<string, AdminModelPricing>;
  ollama: Record<string, AdminModelPricing>;
  jina: AdminJinaPricing;
  tavily?: AdminTavilyPricing;
}

export interface AdminPricingResponse {
  pricing: AdminPricingConfig;
  updated_at: string | null;
  updated_by: string | null;
}

/** GET ``/admin/settings/pricing`` — current model pricing config. */
export async function getAdminPricing(): Promise<AdminPricingResponse> {
  return apiClient.get<AdminPricingResponse>("/admin/settings/pricing");
}

/** PUT ``/admin/settings/pricing`` — save model pricing config. */
export async function updateAdminPricing(
  pricing: AdminPricingConfig,
): Promise<AdminPricingResponse> {
  return apiClient.put<AdminPricingResponse>("/admin/settings/pricing", {
    pricing,
  });
}

// ── Markup % (Jul 27, 2026) — platform-admin margin on raw LLM cost ─────────
// The founder sets a MARKUP PERCENTAGE applied on top of the raw LLM cost;
// customers then see usage priced WITH the markup, always as a % (never raw
// euros). Global default unless a ``tenant_id`` scope is passed.

export interface AdminMarkupResponse {
  /** Effective markup %, applied on top of the raw LLM cost. */
  markup_pct: number;
  /** ``null`` = the global default markup. */
  tenant_id: string | null;
  /** Legal bounds the UI validates against (from the BE, not hard-coded). */
  min_pct: number;
  max_pct: number;
}

function _markupQs(tenantId?: string): string {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
}

/** GET markup % — global default, or a per-tenant override when ``tenantId`` set. */
export async function getAdminMarkup(tenantId?: string): Promise<AdminMarkupResponse> {
  return apiClient.get<AdminMarkupResponse>(`/platform-admin/pricing/markup${_markupQs(tenantId)}`);
}

/** PUT markup % (0–500) — global default, or a per-tenant override. */
export async function updateAdminMarkup(
  markupPct: number,
  tenantId?: string,
): Promise<AdminMarkupResponse> {
  return apiClient.put<AdminMarkupResponse>(
    `/platform-admin/pricing/markup${_markupQs(tenantId)}`,
    { markup_pct: markupPct },
  );
}

/** Models v2 has its own frozen tariff. This rate does not change legacy Engine pricing. */
export interface AdminModelMarkupResponse extends AdminMarkupResponse {
  /** True only when this organization has an explicit rate. */
  is_override: boolean;
}

export async function getAdminModelMarkup(tenantId?: string): Promise<AdminModelMarkupResponse> {
  return apiClient.get<AdminModelMarkupResponse>(`/platform-admin/pricing/model-markup${_markupQs(tenantId)}`);
}

export async function updateAdminModelMarkup(
  markupPct: number,
  tenantId?: string,
): Promise<AdminModelMarkupResponse> {
  return apiClient.put<AdminModelMarkupResponse>(
    `/platform-admin/pricing/model-markup${_markupQs(tenantId)}`,
    { markup_pct: markupPct },
  );
}

export async function resetAdminModelMarkup(tenantId: string): Promise<AdminModelMarkupResponse> {
  return apiClient.delete<AdminModelMarkupResponse>(
    `/platform-admin/pricing/model-markup${_markupQs(tenantId)}`,
  );
}

// ── Metronome billing control-plane (Aug 7, 2026) ──────────────────────────
// The founder drives the whole usage-billing lifecycle from Admin — global +
// per-tenant markup %, settlement channel, prepaid credits, invoices — without
// opening the Metronome or Stripe dashboards. The one dashboard-only step (the
// account-level Stripe⇄Metronome link) is surfaced as ``stripe_connect_url``.

export type SettlementMode = "stripe_auto" | "stripe_invoice" | "external";

export interface MetronomeStatus {
  /** ``false`` ⇒ METRONOME_API_KEY not set; the rest of the page is inert. */
  configured: boolean;
  /** Whether the billable metric + product + rate card exist yet. */
  has_base_pricing: boolean;
  rate_card_id?: string | null;
  global_markup_pct?: number | null;
  /** Deep-link to the one-time Stripe⇄Metronome connect wizard. */
  stripe_connect_url?: string;
}

export interface MetronomeProvisionResult {
  rate_card_id: string;
  product_id: string;
  metric_id: string;
  global_markup_pct: number;
}

export interface MetronomeMarkup {
  /** ``null`` = inherits the global rate (no per-tenant override). */
  markup_pct: number | null;
  tenant_id?: string;
}

export interface MetronomeSettlement {
  tenant_id: string;
  config: Record<string, unknown> | null;
}

export interface MetronomeInvoice {
  id: string;
  status?: string;
  type?: string;
  total?: number;
  start_timestamp?: string;
  end_timestamp?: string;
  [key: string]: unknown;
}

export interface MetronomeInvoicesResult {
  tenant_id: string;
  invoices: MetronomeInvoice[];
}

export interface MetronomeBalancesResult {
  tenant_id: string;
  balances: Array<Record<string, unknown>>;
}

/** GET ``/platform-admin/metronome/status`` — setup snapshot for the checklist. */
export async function getMetronomeStatus(): Promise<MetronomeStatus> {
  return apiClient.get<MetronomeStatus>("/platform-admin/metronome/status");
}

/** POST ``/platform-admin/metronome/provision`` — create metric+product+rate card. */
export async function provisionMetronome(
  globalMarkupPct: number,
): Promise<MetronomeProvisionResult> {
  return apiClient.post<MetronomeProvisionResult>("/platform-admin/metronome/provision", {
    global_markup_pct: globalMarkupPct,
  });
}

/** GET ``/platform-admin/metronome/pricing/global`` — current global markup %. */
export async function getMetronomeGlobalMarkup(): Promise<MetronomeMarkup> {
  return apiClient.get<MetronomeMarkup>("/platform-admin/metronome/pricing/global");
}

/** PUT ``/platform-admin/metronome/pricing/global`` — set the global markup %. */
export async function updateMetronomeGlobalMarkup(markupPct: number): Promise<MetronomeMarkup> {
  return apiClient.put<MetronomeMarkup>("/platform-admin/metronome/pricing/global", {
    markup_pct: markupPct,
  });
}

/** GET per-tenant markup override (``null`` = inherits global). */
export async function getMetronomeTenantMarkup(tenantId: string): Promise<MetronomeMarkup> {
  return apiClient.get<MetronomeMarkup>(
    `/platform-admin/metronome/pricing/tenant/${encodeURIComponent(tenantId)}`,
  );
}

/** PUT per-tenant markup override. */
export async function updateMetronomeTenantMarkup(
  tenantId: string,
  markupPct: number,
): Promise<MetronomeMarkup> {
  return apiClient.put<MetronomeMarkup>(
    `/platform-admin/metronome/pricing/tenant/${encodeURIComponent(tenantId)}`,
    { markup_pct: markupPct },
  );
}

/** GET the tenant's settlement config. */
export async function getMetronomeSettlement(tenantId: string): Promise<MetronomeSettlement> {
  return apiClient.get<MetronomeSettlement>(
    `/platform-admin/metronome/settlement/${encodeURIComponent(tenantId)}`,
  );
}

/** PUT the tenant's settlement channel (Stripe auto/invoice or external). */
export async function updateMetronomeSettlement(
  tenantId: string,
  mode: SettlementMode,
): Promise<MetronomeSettlement> {
  return apiClient.put<MetronomeSettlement>(
    `/platform-admin/metronome/settlement/${encodeURIComponent(tenantId)}`,
    { mode },
  );
}

/** POST a prepaid/free credit (USD) to a tenant. */
export async function grantMetronomeCredit(
  tenantId: string,
  amountUsd: number,
  name?: string,
): Promise<Record<string, unknown>> {
  return apiClient.post<Record<string, unknown>>(
    `/platform-admin/metronome/credits/${encodeURIComponent(tenantId)}`,
    { amount_usd: amountUsd, name },
  );
}

/** GET a tenant's invoices. */
export async function getMetronomeInvoices(tenantId: string): Promise<MetronomeInvoicesResult> {
  return apiClient.get<MetronomeInvoicesResult>(
    `/platform-admin/metronome/invoices/${encodeURIComponent(tenantId)}`,
  );
}

/** GET a tenant's credit/commit balances. */
export async function getMetronomeBalances(tenantId: string): Promise<MetronomeBalancesResult> {
  return apiClient.get<MetronomeBalancesResult>(
    `/platform-admin/metronome/balances/${encodeURIComponent(tenantId)}`,
  );
}

// ── Central model registry (Jul 2026) — edit every model slot from admin ────

/** One overridable model slot + its current effective value. */
export interface AdminModelRegistrySlot {
  key: string;
  label: string;
  category: string;
  env_default: string;
  effective: string;
  overridden: boolean;
}

export interface AdminModelRegistryResponse {
  slots: AdminModelRegistrySlot[];
  updated_at: string | null;
  updated_by: string | null;
}

/** GET ``/admin/settings/model-registry`` — catalog + current overrides. */
export async function getAdminModelRegistry(): Promise<AdminModelRegistryResponse> {
  return apiClient.get<AdminModelRegistryResponse>("/admin/settings/model-registry");
}

/** PUT ``/admin/settings/model-registry`` — save per-slot model overrides. */
export async function updateAdminModelRegistry(
  overrides: Record<string, string>,
): Promise<AdminModelRegistryResponse> {
  return apiClient.put<AdminModelRegistryResponse>("/admin/settings/model-registry", {
    overrides,
  });
}

// ── Usage tier plans ─────────────────────────────────────────────────────

export interface AdminUsageTierPlan {
  /** UUID. */
  id: string;
  multiplier: number;
  display_name: string;
  price_per_user_eur: number;
  /** Chat cap (sempre attivo, mai null). */
  cost_cap_eur: number;
  /**
   * Ingestion cap opzionale (May 13, 2026 — bucket-split SOTA).
   * ``null`` = illimitato; finite ≥ 0 = secondo cap enforcement.
   * Per impostare unlimited via PUT usa la sentinella ``-1`` (l'endpoint
   * la traduce in NULL).
   */
  ingestion_cap_eur: number | null;
  storage_limit_bytes: number;
  storage_gb: number;
  is_active: boolean;
  sort_order: number | null;
}

/**
 * Patch shape per ``PUT /admin/plans/{id}``. ``ingestion_cap_eur``
 * accetta tre valori:
 *   - ``undefined`` → campo non passato (mantieni il valore attuale)
 *   - finite number ≥ 0 → imposta cap finito
 *   - ``-1`` (sentinella) → imposta unlimited (DB NULL)
 *
 * Pattern necessario perché Pydantic ``exclude_none=True`` lato BE non
 * distingue "campo non passato" da "campo passato a None".
 */
export type AdminUpdatePlanPatch = Partial<
  Pick<
    AdminUsageTierPlan,
    | "display_name"
    | "price_per_user_eur"
    | "cost_cap_eur"
    | "storage_gb"
    | "multiplier"
    | "sort_order"
  >
> & {
  ingestion_cap_eur?: number; // see docstring for -1 sentinel
};

export interface AdminCreatePlanRequest {
  display_name: string;
  multiplier: number;
  price_per_user_eur: number;
  cost_cap_eur: number;
  storage_gb: number;
  /**
   * Ingestion cap opzionale alla creazione. Omitti (o passa ``null``)
   * per default unlimited; valore finito per cap di enforcement.
   */
  ingestion_cap_eur?: number | null;
}

/** GET ``/admin/plans`` — list every usage tier plan. */
export async function listAdminPlans(): Promise<AdminUsageTierPlan[]> {
  return apiClient.get<AdminUsageTierPlan[]>("/admin/plans");
}

/** PUT ``/admin/plans/{id}`` — patch a plan in place. */
export async function updateAdminPlan(
  planId: string,
  patch: AdminUpdatePlanPatch,
): Promise<AdminUsageTierPlan> {
  return apiClient.put<AdminUsageTierPlan>(`/admin/plans/${encodeURIComponent(planId)}`, patch);
}

/** POST ``/admin/plans`` — create a new usage tier plan. */
export async function createAdminPlan(body: AdminCreatePlanRequest): Promise<AdminUsageTierPlan> {
  return apiClient.post<AdminUsageTierPlan>("/admin/plans", body);
}

/** DELETE ``/admin/plans/{id}`` — remove a plan. */
export async function deleteAdminPlan(planId: string): Promise<void> {
  return apiClient.delete<void>(`/admin/plans/${encodeURIComponent(planId)}`);
}
